import type { ElementCategory } from "@/data/elements";

/**
 * Curated set of categories playable in Element Battle's Category mode.
 *
 * Each entry maps to a predicate over an element. We also tag each category
 * with a difficulty (1–3 stars) and a suggested grade band so teachers can
 * pick a fair pool for their class without having to count members.
 */

export type GameCategoryKey =
  | "noble-gas"
  | "halogen"
  | "alkali-metal"
  | "alkaline-earth"
  | "transition-metal"
  | "metalloid"
  | "nonmetal"
  | "lanthanide"
  | "actinide";

export type Difficulty = 1 | 2 | 3;

export type GameCategory = {
  key: GameCategoryKey;
  label: string;
  hint: string;
  difficulty: Difficulty;
  /** Suggested grade band, e.g. "Gr 9–10". */
  gradeBand: string;
  /** Set of ElementCategory values that count as a match. */
  matches: ElementCategory[];
};

export const GAME_CATEGORIES: GameCategory[] = [
  {
    key: "noble-gas",
    label: "Noble gases",
    hint: "Group 18 - full valence shell",
    difficulty: 1,
    gradeBand: "Gr 9–10",
    matches: ["noble-gas"],
  },
  {
    key: "alkali-metal",
    label: "Alkali metals",
    hint: "Group 1 (excluding hydrogen)",
    difficulty: 1,
    gradeBand: "Gr 9–10",
    matches: ["alkali-metal"],
  },
  {
    key: "halogen",
    label: "Halogens",
    hint: "Group 17 - one electron shy",
    difficulty: 1,
    gradeBand: "Gr 9–10",
    matches: ["halogen"],
  },
  {
    key: "alkaline-earth",
    label: "Alkaline earth metals",
    hint: "Group 2",
    difficulty: 1,
    gradeBand: "Gr 9–10",
    matches: ["alkaline-earth"],
  },
  {
    key: "metalloid",
    label: "Metalloids",
    hint: "Properties between metals and nonmetals",
    difficulty: 2,
    gradeBand: "Gr 10–11",
    matches: ["metalloid"],
  },
  {
    key: "nonmetal",
    label: "Nonmetals",
    hint: "Includes the diatomic nonmetals (excl. halogens & noble gases)",
    difficulty: 2,
    gradeBand: "Gr 10–11",
    matches: ["nonmetal"],
  },
  {
    key: "transition-metal",
    label: "Transition metals",
    hint: "d-block elements",
    difficulty: 3,
    gradeBand: "Gr 11–12",
    matches: ["transition-metal"],
  },
  {
    key: "lanthanide",
    label: "Lanthanides",
    hint: "f-block, atomic numbers 57–71",
    difficulty: 3,
    gradeBand: "Gr 11–12",
    matches: ["lanthanide"],
  },
  {
    key: "actinide",
    label: "Actinides",
    hint: "f-block, atomic numbers 89–103",
    difficulty: 3,
    gradeBand: "Gr 11–12",
    matches: ["actinide"],
  },
];

export const getCategory = (key: GameCategoryKey): GameCategory =>
  GAME_CATEGORIES.find((c) => c.key === key) ?? GAME_CATEGORIES[0];

/** Render helper: 3-star rating like "★★☆". */
export function difficultyStars(d: Difficulty): string {
  return "★★★☆☆☆".slice(3 - d, 3 - d + 3);
}
